import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import { NotificationsMenu } from '../notifications';
import { dismissAllNotifications } from '../../store/redux/actions/notificationActions';
import UserMenu from './UserMenu';
import LanguageSwitch from './LanguageSwitch';
import TopBarPendingActions from '../pendingActions/TopBarPendingActions';
import { navigationModules } from './navigationModules';

interface TopBarProps {
  /** Callback to open/close the mobile sidebar */
  onMobileMenuClick: () => void;
}

interface RootState {
  user: {
    companyName: string;
    userId: string;
  };
}

/**
 * Top navigation bar with search, notifications, language switch, and user menu.
 *
 * Integrations:
 * - NotificationsMenu: Uses existing SignalR-connected component
 * - UserMenu: Shows real user data with profile/settings/logout
 * - LanguageSwitch: Integrates with existing i18n system
 * - Search: Functional page search using navigationModules
 */
const TopBar: React.FC<TopBarProps> = ({ onMobileMenuClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);

  const { companyName, userId } = useSelector((state: RootState) => state.user);

  // Build searchable pages list from navigation modules
  const searchablePages = useMemo(() => {
    const pages: Array<{ title: string; path: string; module: string }> = [];
    navigationModules.forEach(module => {
      module.pages.forEach(page => {
        pages.push({
          title: page.title,
          path: page.path,
          module: module.title,
        });
      });
    });
    return pages;
  }, []);

  // Filter pages based on search query
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return searchablePages
      .filter(
        page =>
          page.title.toLowerCase().includes(query) ||
          page.module.toLowerCase().includes(query)
      )
      .slice(0, 8); // Limit to 8 results
  }, [searchQuery, searchablePages]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchResults.length > 0) {
      navigate(searchResults[0].path);
      setSearchQuery('');
      setShowSearchResults(false);
    }
  };

  const handleSearchResultClick = (path: string) => {
    navigate(path);
    setSearchQuery('');
    setShowSearchResults(false);
  };

  return (
    <header className="h-16 min-h-16 flex-shrink-0 bg-card border-b border-border flex items-center justify-between px-4 md:px-6">
      {/* Left Section - Mobile Menu + Search */}
      <div className="flex items-center space-x-3 rtl:space-x-reverse flex-1">
        {/* Mobile Menu Button */}
        <button
          onClick={onMobileMenuClick}
          className="md:hidden p-2 min-h-[44px] min-w-[44px] rounded-lg hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          title={t('common:Open menu') || 'Open menu'}
        >
          <Icon name="Menu" size={20} className="text-muted-foreground" />
        </button>

        {/* Search */}
        <div className="relative flex-1 max-w-xl">
          <form onSubmit={handleSearch}>
            <Icon
              name="Search"
              size={18}
              className="absolute start-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
            <input
              type="text"
              placeholder={t('common:Search pages...') || 'Search pages...'}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSearchResults(true);
              }}
              onFocus={() => {
                setShowSearchResults(true);
              }}
              onBlur={() => {
                // Delay hiding results to allow click
                setTimeout(() => setShowSearchResults(false), 200);
              }}
              className="w-full ps-10 pe-4 py-2 border border-border hover:border-foreground/30 rounded-lg text-sm text-foreground bg-background transition-all focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:border-primary focus:outline-none placeholder:text-muted-foreground"
            />
          </form>

          {/* Search Results Dropdown */}
          {showSearchResults && searchResults.length > 0 && (
            <div className="absolute top-full left-0 rtl:left-auto rtl:right-0 w-full mt-1 bg-popover rounded-lg shadow-lg border border-border z-50 max-h-80 overflow-y-auto">
              {searchResults.map((result, index) => (
                <button
                  key={`${result.path}-${index}`}
                  onMouseDown={() => handleSearchResultClick(result.path)}
                  className="w-full px-4 py-3 text-start hover:bg-muted transition-colors border-b border-border last:border-0 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <p className="text-sm font-medium text-foreground">{result.title}</p>
                  <p className="text-xs text-muted-foreground">{result.module}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right Section - Notifications, Language, User Menu */}
      <div className="flex items-center space-x-2 md:space-x-4 rtl:space-x-reverse">
        {/* Pending actions count — hidden when the user has nothing waiting */}
        <TopBarPendingActions />

        {/* Notifications - Using existing mowazaf component with SignalR */}
        <NotificationsMenu
          companyName={companyName}
          userId={userId}
          dismissAll={dismissAllNotifications}
          onNavigate={(path: string) => navigate(path)}
        />

        {/* Language Switch */}
        <LanguageSwitch />

        {/* User Menu */}
        <UserMenu />
      </div>
    </header>
  );
};

export default TopBar;
